import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { gql } from "@apollo/client";
import type { SyncablePriceListTariff } from "elephant-web/gql";
import { useMemo } from "react";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { PriceListTariff as RxPriceListTariff } from "../collections/price-list-tariff";
import type { RxDBCollections } from "../provider";
import {
  SyncPriceListTariffPullDocument,
  type WholeSyncablePriceListTariffFragment,
} from "./__generated__/price-list-tariff.gql";
import {
  type ReplicationPullConfig,
  useGqlReplication,
} from "./use-gql-replication";

export const useReplicatePriceListTariff = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<RxPriceListTariff>;
}) => {
  const [enabled] = useFeatureFlag<boolean>(featureFlags.billing.priceListRxdb);

  const pull = useMemo<
    ReplicationPullConfig<WholeSyncablePriceListTariffFragment> | undefined
  >(() => {
    if (!enabled) return undefined;
    return {
      query: SyncPriceListTariffPullDocument,
      buildInputVariables: async (checkpoint, batchSize) => ({
        input: {
          limit: batchSize,
          checkpoint,
        },
      }),
      buildPullCheckpoint: async (doc) => ({
        id: doc.id,
        updatedAt: doc.meta.updatedAt,
      }),
      reSyncInterval: { hours: 1 },
    };
  }, [enabled]);

  return useGqlReplication<RxPriceListTariff, SyncablePriceListTariff, null>({
    db,
    collection,
    pull,
    mapGqlToRx: mapGqlPriceListTariffToRxPriceListTariff,
    batchSize: 100,
    metadataLocalStorageKeySuffix: "price-list-tariff",
  });
};

export const mapGqlPriceListTariffToRxPriceListTariff = (
  tariff: WholeSyncablePriceListTariffFragment,
): WithDeleted<RxPriceListTariff> => ({
  id: tariff.id,
  name: tariff.name,
  meta: {
    createdAt: tariff.meta.createdAt,
    createdByUserId: tariff.meta.createdByUserId,
    updatedAt: tariff.meta.updatedAt,
    updatedByUserId: tariff.meta.updatedByUserId,
  },
  _deleted: tariff.meta.deleted,
});

gql`
  fragment WholeSyncablePriceListTariff on SyncablePriceListTariff {
    id
    name
    meta {
      createdAt
      createdByUserId
      updatedAt
      updatedByUserId
      deleted
    }
  }
`;

gql`
  query SyncPriceListTariffPull($input: PullPriceListTariffInput!) {
    pullPriceListTariff(input: $input) {
      checkpoint {
        id
        updatedAt
      }
      documents {
        ...WholeSyncablePriceListTariff
      }
    }
  }
`;
